import { Alert, Box, Button, Skeleton, Typography, useTheme } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { PersonaStep } from "../dtos";
import { useStepDetail } from "../hooks/use-step-detail.hook";
import { useStepMutations } from "../hooks/use-step-mutations.hook";
import CoApplicantsCard from "./co-applicants-card";
import { ComparisonPanel } from "./comparison-panel";
import StepForm from "./step-form";
import {PaymentPlanUpdate} from './payment-plan-update';

/**
 * Lazily loads the internal step detail (submissions, uploads, prefill, actions) once the
 * card is expanded, then renders the dynamic form. The agent console prefers the FULL
 * internal formSchema from the detail; the persona-scoped schema is the fallback.
 */
interface StepBodyProps {
  step: PersonaStep;
  caseUid: string | null;
  participantUid: string | null;
  customerUid: string | null;
}

const StepBody = (props: StepBodyProps) => {
  const [reviewSession, setReviewSession] = useState(0);
  return <StepBodyContent {...props} key={reviewSession} onReloadPaymentReview={() => setReviewSession(value => value + 1)} />;
};

const StepBodyContent = ({step, caseUid, participantUid, customerUid, onReloadPaymentReview}: StepBodyProps & {onReloadPaymentReview: () => void}) => {
  const theme = useTheme();
  const { detail, isLoading } = useStepDetail({
    stepUid: step.uid,
    enabled: true,
  });
  const [reviewSnapshot, setReviewSnapshot] = useState<{uid: string; version?: number} | null>(() =>
    detail?.uid === step.uid ? {uid: step.uid, version: detail.paymentPlanUpdateVersion} : null);
  // Preserve the version paired with this form's values, including a pre-revision form.
  // The explicit reload remounts both the form and its mutation hooks together.
  useEffect(() => {
    if (detail?.uid === step.uid && reviewSnapshot?.uid !== step.uid) {
      setReviewSnapshot({uid: step.uid, version: detail.paymentPlanUpdateVersion});
    }
  }, [detail, step.uid, reviewSnapshot]);
  const paymentReviewChanged = reviewSnapshot?.uid === step.uid && detail?.uid === step.uid &&
    detail.paymentPlanUpdateVersion !== undefined && reviewSnapshot.version !== detail.paymentPlanUpdateVersion;
  const mutations = useStepMutations({
    stepUid: step.uid,
    paymentPlanUpdateVersion: reviewSnapshot?.version,
    customerUid,
    caseUid,
    participantUid,
  });

  const schema = useMemo(() => {
    if (Array.isArray(detail?.formSchema) && detail!.formSchema!.length) {
      return detail!.formSchema!;
    }
    return step.formSchema ?? [];
  }, [detail, step.formSchema]);

  if (isLoading) {
    return <Skeleton variant="rounded" width="100%" height={120} />;
  }

  // §Background-refetch (2026-08-08): react-query flips `isError` on a FAILED BACKGROUND REFETCH while
  // `data` is still held, so this used to replace a mounted, filled-in form with an error line — throwing
  // away everything typed since the last autosave, including the client mobile, which autosave
  // deliberately never persists. The error state may only render when there is genuinely nothing to show;
  // a refetch that fails over good data leaves the form alone (the next poll or SSE event recovers it).
  if (!detail) {
    return (
      <Typography
        variant="body2"
        color={theme.nawy.colors.semantic.text.extension.negative}
      >
        Failed to load step details.
      </Typography>
    );
  }

  // §Participants: the co-applicants display + capture live on the step the builder flags with
  // uiFlags.participantCapture (the Client Profile step) — same placement as workflow-manager's
  // own step screen.
  // §CoApplicantEntry: the card offers the types the step is CONFIGURED to capture, guarantor
  // included (it used to be a lone button in the Loan process header). Falls back to JOINT for a
  // step from an older WM deploy whose config lists nothing.
  const captureTypes = (step.uiFlags?.participantCapture?.types ?? []).filter(
    (type): type is "JOINT" | "GUARANTOR" =>
      type === "JOINT" || type === "GUARANTOR"
  );
  const captureJoint = captureTypes.includes("JOINT");

  if (caseUid && (detail.uiFlags?.paymentPlanUpdate as {enabled?: boolean} | undefined)?.enabled) {
    return <><PaymentPlanUpdate caseUid={caseUid} inlineStep={detail} /><ComparisonPanel comparisons={detail.comparisons ?? []} /></>;
  }


  if (paymentReviewChanged) {
    return <Alert severity="warning" action={<Button color="inherit" onClick={onReloadPaymentReview}>Reload latest review</Button>}>
      The payment plan changed while this review was open. Reload to review the latest plan before saving or approving. Unsaved edits from the previous review will be discarded.
    </Alert>;
  }

  return (
    <>
      {detail.paymentPlanUpdateBlockReason && <Alert severity="info">{detail.paymentPlanUpdateBlockReason}</Alert>}
      <ComparisonPanel comparisons={detail.comparisons ?? []} />
      <StepForm
        step={step}
        caseUid={caseUid}
        detail={detail}
        schema={schema}
        mutations={mutations}
      />
      {captureJoint && !detail.paymentPlanUpdateBlockReason && (
        <Box sx={{ mt: 2 }}>
          <CoApplicantsCard
            caseUid={caseUid}
            addTypes={captureTypes.length ? captureTypes : ["JOINT"]}
          />
        </Box>
      )}
    </>
  );
};

export default StepBody;
